/**
 * User-visible latency of one chat turn: from the moment the user sends a message to the
 * moment the UI shows its result (Sayer's reply, a UI action, or the preprocessed
 * contract panel).
 *
 * Server spans measure each HTTP request on its own, but a turn is several requests plus
 * browser work, and nothing on the server knows where it began or what the user saw. So
 * the browser keeps the clock:
 *
 *   const turn = beginChatTurn();          // on send
 *   turn.route("classify");                // which branch of the routing ladder ran
 *   const done = turn.phase("classify");   // ...around an awaited step
 *   done();
 *   turn.finish("dispatched", { intent }); // when the result is on screen
 *
 * `finish` beacons ONE record to `POST /api/telemetry/chat-turn`, which the server
 * records as a `chat.turn` span tree in `observability.spans`. Each request a turn makes
 * carries `x-chat-turn-id` (`chatTurnHeaders(turn.id)`, threaded explicitly from the turn
 * that made it), so the server spans of every request the turn made join back to it.
 *
 * Times are milliseconds RELATIVE to the turn's start, from the monotonic
 * `performance.now()` clock, so client/server clock skew never enters the numbers.
 *
 * PII: the record carries names from fixed vocabularies and numbers. Never message text.
 * Telemetry must never affect the chat: every entry point is a no-op on failure.
 */
import { reportFailure } from "./failureCounts.js";

/** Header that ties a request to the open turn. Must match the server's reader. */
export const CHAT_TURN_HEADER = "x-chat-turn-id";

export const CHAT_TURN_ENDPOINT = "/api/telemetry/chat-turn";

/**
 * The intents a turn may report — `classifyChatIntent`'s action list. An intent is model
 * output, so it is checked against this list rather than trusted: anything else is
 * reported as `other`, never verbatim. Mirrored by the server's vocabulary (pinned by
 * server/lib/__tests__/chatTurnTelemetry.test.js).
 */
export const CHAT_INTENTS = Object.freeze([
  "new_forecast",
  "confirm_new_forecast",
  "set_engine",
  "add_event",
  "branch_occurs",
  "branch_not_occurs",
  "add_index",
  "remove_index",
  "change_indices",
  "zoom_out",
  "zoom_to_timeline",
  "center",
  "set_detail",
  "show_report",
  "open_forecast_manager",
  "file_bug",
  "open_settings",
  "open_account",
  "open_series_panel",
  "close_panels",
  "explain",
  "reset",
]);

/** A turn left open longer than this is abandoned rather than reported (a stalled
 *  promise, a closed tab): its number would describe the bug, not the latency. */
const MAX_TURN_MS = 10 * 60 * 1000;

let current = null;

/**
 * Run `cb` once the browser has painted what the caller just dispatched.
 *
 * `finish` is called straight after a store/React update, before React has committed it
 * and before the browser painted it — timing there would leave render latency out of a
 * number that claims "result on screen". So the stamp waits: a macrotask (lets React's
 * scheduler flush the render), then the next animation frame (the paint containing it),
 * then a task after that frame. This lands on the first paint that includes the result,
 * give or take one frame.
 *
 * A hidden tab never fires animation frames, so there the stamp is taken at once — the
 * user is not watching the paint that would otherwise be waited for. That holds for a tab
 * hidden WHILE waiting too: the pending frame would be suspended until the tab returns, so
 * a visibility change to hidden stamps immediately instead. `cb` runs exactly once, with
 * `{ unloading: true }` when that visibility change fired it — the page may be going away
 * (a reload, a navigation), and the beacon it sends may be torn down with it.
 */
function defaultAfterPaint(cb) {
  const raf = typeof requestAnimationFrame === "function" ? requestAnimationFrame : null;
  const doc = typeof document !== "undefined" ? document : null;
  if (!raf || doc?.visibilityState === "hidden") {
    cb({ unloading: false });
    return;
  }
  let fired = false;
  const onVisibility = () => {
    if (doc.visibilityState === "hidden") fire(true);
  };
  function fire(unloading = false) {
    if (fired) return;
    fired = true;
    doc?.removeEventListener?.("visibilitychange", onVisibility);
    cb({ unloading });
  }
  doc?.addEventListener?.("visibilitychange", onVisibility);
  setTimeout(() => raf(() => setTimeout(fire, 0)), 0);
}

let afterPaint = defaultAfterPaint;

/**
 * True from `pagehide` until `pageshow`: the document is being unloaded (a reload, a
 * navigation, a tab close), and a beacon still in flight is torn down with it. `pagehide`
 * never fires on a tab switch, so this flag never silences a hidden-but-alive tab's failed
 * beacon (only the per-send `unloading` from a hidden-while-waiting `afterPaint` does); a
 * page restored from the back/forward cache reports again.
 */
let pageGone = false;
let pageLifecycleWatched = false;

/** Listen for `pagehide` / `pageshow` once, before the first beacon goes out. */
function watchPageLifecycle() {
  if (pageLifecycleWatched) return;
  const win = typeof window !== "undefined" ? window : null;
  if (typeof win?.addEventListener !== "function") return;
  pageLifecycleWatched = true;
  win.addEventListener("pagehide", () => {
    pageGone = true;
  });
  win.addEventListener("pageshow", () => {
    pageGone = false;
  });
}

function now() {
  return typeof performance !== "undefined" && performance.now ? performance.now() : Date.now();
}

function newId() {
  try {
    if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  } catch {
    /* fall through */
  }
  return null;
}

/**
 * @param {object} record the finished turn
 * @param {{unloading?: boolean}} [ctx] `unloading`: the hidden/unload path sent it (afterPaint)
 * @param {(url: string, init: RequestInit) => Promise<unknown>} [send]
 */
function defaultSend(
  record,
  { unloading = false } = {},
  send = typeof fetch !== "undefined" ? fetch : null,
) {
  if (!send) return;
  try {
    watchPageLifecycle();
    // keepalive: a turn that ends as the user navigates away is still delivered.
    Promise.resolve(
      send(CHAT_TURN_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(record),
        keepalive: true,
      }),
    )
      .then((res) => {
        // A 4xx/5xx RESOLVES `fetch`: a refused beacon is as lost as a rejected one, under the
        // same teardown rule as the catch below. A `send` that answers nothing (a test double)
        // gives no status to judge.
        if (unloading || pageGone) return;
        if (res && typeof res === "object" && "ok" in res && !res.ok) {
          reportFailure(
            "chat_turn.send_failed",
            new Error(`POST ${CHAT_TURN_ENDPOINT} answered status ${res.status}`),
          );
        }
      })
      .catch((err) => {
        // A send the hidden/unload fire triggered, or one still in flight when the page began
        // unloading (`pagehide`), is torn down with the page on a reload or navigation; its
        // rejection is the teardown, not a lost beacon. Every other rejection is a lost
        // `chat.turn` span: logged and counted.
        if (unloading || pageGone) return;
        reportFailure("chat_turn.send_failed", err);
      });
  } catch {
    /* never let telemetry reach the caller */
  }
}

/**
 * A fresh client message id (`msg_id`): it rides in the persisted message's metadata and
 * on the `chat.send` / `submit.question` events, so a timeline joins the two. A UUID, or a
 * time + random id where `crypto.randomUUID` is unavailable (non-secure contexts).
 */
export function newMessageId() {
  return newId() ?? `m-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

class ChatTurn {
  constructor(id, send) {
    this.id = id;
    /** The user message this turn answers and its text; set by hooks/useChatIntent.js. */
    this.msgId = null;
    this.msgText = null;
    /** True when the message was an example question the user clicked (hooks/useChatIntent.js). */
    this.fromExample = false;
    /** The control the message was sent from (`chat.send`, `ask-hero.submit`, …; useChatIntent). */
    this.origin = null;
    /**
     * Where this turn's replies are filed (`lib/forecast/chatTarget.ts`), captured at send by
     * hooks/useChatIntent.js. Every line the turn writes after an await goes here, never to
     * whatever is selected when it lands.
     */
    this.target = null;
    this._send = send;
    this._t0 = now();
    this._route = "unrouted";
    this._phases = [];
    this._done = false;
    /** Listeners told of the first visible mark and of the end (hooks/useChatIntent.js). */
    this._watchers = [];
  }

  /**
   * Follow this turn: `onMark(name)` for each instant marked visible, `onEnd(outcome)` once
   * when it finishes or is superseded (the `chat.ask` intent ends with it). Never throws
   * into the turn.
   */
  watch({ onMark = null, onEnd = null } = {}) {
    if (!this._done) this._watchers.push({ onMark, onEnd });
    return this;
  }

  /**
   * Resolves once this turn has ended: its result is on screen, or it was superseded. At once
   * if it already has. For a caller that started the turn but not the work it hands off to
   * (submit, the pre-forecast answer), which the turn outlives.
   */
  ended() {
    if (this._done) return Promise.resolve();
    return new Promise((resolve) => this._watchers.push({ onEnd: () => resolve() }));
  }

  _tell(kind, value) {
    for (const w of this._watchers) {
      try {
        w[kind]?.(value);
      } catch {
        /* a watcher never breaks the turn */
      }
    }
  }

  /** Which branch of the routing ladder handled the message. Last call wins. */
  route(name) {
    if (!this._done) this._route = name;
    return this;
  }

  /** The route set so far. */
  get routeName() {
    return this._route;
  }

  /**
   * Record an instant: something became visible. Stamped when it is PAINTED (see
   * `afterPaint`), not when the caller dispatched it.
   */
  mark(name) {
    if (this._done) return;
    const entry = { name, start_ms: null, end_ms: null };
    this._phases.push(entry);
    afterPaint(() => {
      entry.start_ms = entry.end_ms = Math.round(now() - this._t0);
      // Told when it is visible, so `first_feedback_ms` includes the render.
      this._tell("onMark", name);
    });
  }

  /**
   * Open a timed phase; call the returned function when it ends. Ending twice, or after
   * the turn finished, is harmless.
   */
  phase(name) {
    if (this._done) return () => {};
    const entry = { name, start_ms: Math.round(now() - this._t0), end_ms: null };
    this._phases.push(entry);
    return () => {
      if (entry.end_ms === null) entry.end_ms = Math.round(now() - this._t0);
    };
  }

  /**
   * Close the turn at "the result is on screen" and report it. The turn stops being
   * current at once (so a next message opens a fresh one and requests stop carrying this
   * id), but its end is stamped when the result is PAINTED (see `afterPaint`). Only the
   * first call counts, so an early exit and a later fallback cannot double-report.
   *
   * @param {string} outcome what the user saw, from a fixed vocabulary
   * @param {{intent?: string}} [extra]
   */
  finish(outcome, extra = {}) {
    if (this._done) return;
    this._done = true;
    if (current === this) current = null;
    afterPaint((ctx) => {
      this._report(outcome, extra, ctx);
      this._tell("onEnd", outcome);
    });
  }

  /** Close as superseded: nothing was shown, so the end is now, not a later paint. */
  _supersede() {
    if (this._done) return;
    this._done = true;
    if (current === this) current = null;
    this._report("superseded", {});
    this._tell("onEnd", "superseded");
  }

  _report(outcome, extra, ctx = {}) {
    const total = Math.round(now() - this._t0);
    if (!this.id || total > MAX_TURN_MS) return;
    const record = {
      turn_id: this.id,
      route: this._route,
      outcome,
      total_ms: total,
      // An unfinished phase (or an unpainted mark) ends with the turn: it was still
      // pending when the result showed.
      phases: this._phases.map((p) => ({
        name: p.name,
        start_ms: p.start_ms ?? total,
        end_ms: p.end_ms ?? total,
      })),
    };
    if (extra.intent) record.intent = CHAT_INTENTS.includes(extra.intent) ? extra.intent : "other";
    this._send(record, { unloading: ctx?.unloading === true });
  }
}

/**
 * Start timing a new turn. A turn still open is closed as `superseded` first (the user
 * sent again before it resolved), so at most one turn is ever current.
 *
 * @param {{send?: (record: object, ctx: {unloading: boolean}) => void}} [opts] test seam;
 *   defaults to the beacon. `ctx.unloading` is true when the hidden/unload path sent it.
 */
export function beginChatTurn({ send = defaultSend } = {}) {
  if (current) current._supersede();
  current = new ChatTurn(newId(), send);
  return current;
}

/**
 * The open turn, or null. A step of the turn reads it ONCE, at its start, and passes that
 * object on (e.g. into `submit`) — never re-reads it later, when a newer message may have
 * opened a different turn.
 */
export function currentChatTurn() {
  return current;
}

/**
 * Headers that tie ONE request to the turn that made it. Takes the turn's id explicitly —
 * the caller threads it from the turn it captured — never the "current" turn: async work
 * from an earlier, superseded turn (or a non-chat submit) must not be attributed to
 * whichever turn happens to be open when its request goes out. Empty for no id.
 *
 * @param {string | null | undefined} turnId
 * @returns {Record<string, string>}
 */
export function chatTurnHeaders(turnId) {
  return typeof turnId === "string" && turnId ? { [CHAT_TURN_HEADER]: turnId } : {};
}

/** Test-only: forget the current turn without reporting it, and restore paint timing. */
export function __resetChatTurnForTests() {
  current = null;
  pageGone = false;
  pageLifecycleWatched = false;
  afterPaint = defaultAfterPaint;
}

/** Test-only: replace the paint scheduler (e.g. `(cb) => cb()` to stamp synchronously). */
export function __setAfterPaintForTests(fn) {
  afterPaint = fn;
}
